import classNames from 'classnames'
import {chunk, has} from "lodash"
import { useRouter } from 'next/router'
import { ParsedUrlQuery } from "querystring"
import React from 'react'
import { AiFillHeart, AiOutlineHeart } from "react-icons/ai"
import Skeleton from 'react-loading-skeleton'
import urljoin from 'url-join'
import Breadcrumbs, { BreadcrumbPreviousPage } from '../../../components/breadcrumbs'
import CartQuantitySelect from '../../../components/cart/quantity_select'
import CartMessageInput from '../../../components/cart_message_input'
import HeadTitle from '../../../components/head_title'
import Layout from '../../../components/layout/layout'
import Loading from '../../../components/loading'
import MetaTags from '../../../components/meta_tags'
import AddToCartButton from '../../../components/product/add_to_cart_button'
import OrderFoodMessage from "../../../components/product/order_food_message"
import Ratings from '../../../components/ratings'
import SourceLabel from '../../../components/source_label'
import TimeoutAlert from '../../../components/timeout_alert'
import UnderlineNav from '../../../components/underline_nav'
import { useRegionCountryCode, useRegionLinkPrefix } from '../../../hooks/region'
import { useImageFallbackUrl } from "../../../hooks/use_image_fallback_url"
import { useTfaPassed } from '../../../hooks/user'
import * as gtag from '../../../lib/gtag'
import * as staticProps from '../../../lib/static_props'
import { getSingleProductStaticProps, handleCountryCodeCases } from '../../../lib/static_props'
import Api from '../../../service/api'
import { useCategoriesWrapper } from '../../../service/category'
import { formatMoney } from '../../../service/currency_converter'
import { wrapper } from "../../../store/store"
import styles from '../../../styles/single_product.module.scss'
import { ProductFlags } from "../../../types/api/responses/product"
import {Product, ProductListing} from "../../../types/models/product"
import { extractAxiosError } from '../../../utils/error'
import { genericTextChange } from '../../../utils/forms'
import { imageUrl, thumbUrl } from '../../../utils/image'
import HorizontalScrollingProducts from "../../../components/horizontal_scrolling_products";
import ProductListingCard from "../../../components/product_listing_card";

//removed Specifications from NAV_ITEMS
const NAV_ITEMS = ['About this item'];

function getInitialVariantSettings(p: Product): [boolean[][], string] {
  const newSelectedOptions = new Array(p.variants.length)
  for (const [index, variant] of p.variants.entries()) {
    const selectedState = new Array(variant.options.length)
    selectedState.fill(false)
    selectedState[0] = true
    newSelectedOptions[index] = selectedState
  }

  const variantPathParts = new Array(p.variants.length)
  variantPathParts.fill('0')

  return [newSelectedOptions, variantPathParts.join('/')]
}

interface ProductButtonsProps {
  product: Product;
  quantity: number;
  variantPath: string;
  message: string;
  disabled: boolean;
  flags: ProductFlags | undefined;
}

function ProductButtons({
  product,
  quantity,
  variantPath,
  message,
  disabled,
  flags
}: ProductButtonsProps) {
  const [buttonsErrorMessage, setButtonsErrorMessage] = React.useState<string | null>(null)
  const [buttonsSuccessMessage, setButtonsSuccessMessage] = React.useState<string | null>(null)

  let content;
  if (flags === undefined) {
    content = <Skeleton />
  } else if (flags.isBlocked) {
    content = <p>This product cannot be added to cart</p>
  } else {
    content = <>
      <TimeoutAlert
        message={buttonsSuccessMessage}
        onHide={() => setButtonsSuccessMessage(null)}
        alertColor="success"
      />

      <AddToCartButton
        productId={product.uuid}
        quantity={quantity}
        variantPath={variantPath}
        minQuantity={product.minQuantity}
        maxQuantity={product.maxQuantity}
        message={message}
        onSuccessMessage={setButtonsSuccessMessage}
        onErrorMessage={setButtonsErrorMessage}
        disabled={disabled}
        showBuyNowButton={true}
        isQuotationOnly={flags.isQuotation}
      />
    </>
  }

  return <>
    <TimeoutAlert
      message={buttonsErrorMessage}
      onHide={() => setButtonsErrorMessage(null)}
    />
    {content}
  </>
}

export interface SingleProductPageProps {
  product: Product;
  pageUrl: string;
}

interface ProductAndVariations {
  selectedOptions: boolean[][] | null;
  variantPath: string;
  product: Product;
}

function SingleProductPage(props: SingleProductPageProps) {
  const router = useRouter()

  const tfaPassed = useTfaPassed()
  const regionInfo = useRegionCountryCode()
  const regionLinkPrefix = useRegionLinkPrefix()

  const [productAndVariations, setProductAndVariations] = React.useState<ProductAndVariations>({
    selectedOptions: null,
    variantPath: '',
    product: props.product
  });
  const { selectedOptions, variantPath } = productAndVariations;
  const product = productAndVariations.product || props.product;

  const [mainImage, setMainImage] = React.useState(0)
  const [quantity, setQuantity] = React.useState(1)
  const [activeProductNav, setActiveProductNav] = React.useState(0)
  const [previousPages, setPreviousPages] = React.useState<BreadcrumbPreviousPage[]>([]);
  const [wishlisted, setWishlisted] = React.useState(false)
  const [addMessage, setAddMessage] = React.useState('')
  const [showAddMessage, setShowAddMessage] = React.useState(false)
  const [flags, setFlags] = React.useState<ProductFlags | undefined>();
  const [productRecommendations, setProductRecommendations] = React.useState<ProductListing[]>([]);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null)

  const categories = useCategoriesWrapper()

  React.useEffect(() => {
    if (!props.product) {
      return
    }

    const [selectedOptions, variantPath] = getInitialVariantSettings(props.product);

    setProductAndVariations({
      selectedOptions,
      variantPath,
      product: props.product
    });

    let isEffectValid = true;
    (async function() {
      // console.log('fetch product paramaName: ', paramName)
      try {
        const response = await Api.product.one(props.product.paramName);
        const {product} = response.data
        if (!isEffectValid) {
          return;
        }

        if (product.hasVariant) {
          const [newSelectedOptions, newVariantPath] = getInitialVariantSettings(product)
          setProductAndVariations({
            selectedOptions: newSelectedOptions,
            variantPath: newVariantPath,
            product
          })
        } else {
          setProductAndVariations({
            selectedOptions: [],
            variantPath: '',
            product
          })
        }
      } catch (error) {
        const errorMessage = extractAxiosError(error)
        if (isEffectValid) {
          setErrorMessage('Failed to fetch product, ' + errorMessage)
        }
      }
    })();

    Api.product.getProductFlags(props.product.paramName)
      .then(res => {
        if (isEffectValid) {
          setFlags(res.data.productFlags);
        }
      })
      .catch(error => {
        const errorMessage = extractAxiosError(error)
        if (isEffectValid) {
          setErrorMessage('Failed to fetch product flags, ' + errorMessage);
        }
      });

    Api.product.getRecommendations(props.product.paramName)
      .then(res => {
        if (isEffectValid) {
          setProductRecommendations(res.data.products)
        }
      })
      .catch(error => {
        console.warn('failed to fetch product recommendations', error);
      })
    return () => {
      isEffectValid = false;
    };
  }, [props.product])

  React.useEffect(() => {
    if (!props.product || regionInfo.isLoading) {
      return;
    }

    gtag.viewItem(props.product, regionInfo.countryCode);
  }, [props.product, regionInfo.isLoading, regionInfo.countryCode])

  const isFoodCategory = React.useMemo(() => {
    if (!product) {
      return false;
    }

    return product.category >= 60000 && product.category < 70000;
  }, [product])

  React.useEffect(() => {
    if (!product || !categories) {
      return
    }

    // setup breadcrumbs
    let category = categories.getCategoryById(product.category)
    const newPreviousPages = []
    while (category) {
      newPreviousPages.unshift({
        link: `${regionLinkPrefix}/category/${category.path}`,
        label: category.name,
      })
      category = category.parent
    }
    setPreviousPages(newPreviousPages)
  }, [categories, product, tfaPassed])

  React.useEffect(() => {
    if (!product || !categories || !tfaPassed) {
      return
    }

    // check wishlisted
    Api.wishlist.check(product.uuid)
      .then(res => {
        const { wishlisted } = res.data
        setWishlisted(wishlisted)
      })
      .catch(error => {
        const errorMessage = extractAxiosError(error)
        setErrorMessage('Failed to check wishlist, ' + errorMessage)
      })
  }, [categories, product, tfaPassed])

  const metaImageUrl = React.useMemo(() => {
    let url = ''
    try {
      url = thumbUrl(product.images[0]).toString()
    } catch (error) { }
    return url
  }, [product])

  const handleOptionCheck = React.useCallback((variantIndex: number, optionIndex: number) => {
    if (!selectedOptions) {
      return () => { };
    }

    return () => {
      const newSelectedOptions = selectedOptions.slice()
      newSelectedOptions[variantIndex].fill(false)
      newSelectedOptions[variantIndex][optionIndex] = true

      const steps = newSelectedOptions.map(options => options.indexOf(true))
      const newVariantPath = steps.join('/')

      // set selected variation image
      const variantPathImage = product.variantPaths[newVariantPath].image;
      if (variantPathImage) {
        const imageIndex = product.images.indexOf(variantPathImage);
        if (imageIndex !== -1) {
          setMainImage(imageIndex);
        }
      }

      setProductAndVariations({
        ...productAndVariations,
        selectedOptions: newSelectedOptions,
        variantPath: newVariantPath
      })
    }
  }, [productAndVariations])

  const handleWishlist = React.useCallback(async () => {
    let wishlisted = false;
    try {
      const res = await Api.wishlist.toggle(product.uuid);
      ({ wishlisted } = res.data);
      setWishlisted(wishlisted);
    } catch (error: any) {
      setErrorMessage(extractAxiosError(error));
    }

    if (wishlisted) {
      gtag.addToWishlist(product, regionInfo.countryCode)
    }
  }, [product, regionInfo.countryCode]);

  const { minQuantity, maxQuantity } = React.useMemo(() => {
    const { variantPath, product } = productAndVariations;
    if (!product) {
      return {
        minQuantity: 0,
        maxQuantity: 0
      };
    }

    let { minQuantity, maxQuantity } = product;
    if (variantPath) {
      const hasVariationStock = has(product, ['variantPaths', variantPath, 'stock']);
      if (hasVariationStock) {
        const variationStock = product.variantPaths[variantPath].stock || 0;
        maxQuantity = Math.min(variationStock, maxQuantity);
      }
    }

    return {
      minQuantity: Math.max(minQuantity || 0, 1),
      maxQuantity
    };
  }, [productAndVariations]);

  const imageErrorHandler = useImageFallbackUrl();

  if (router.isFallback) {
    return <Loading />
  }

  let hasStock = false;
  let shownPrice: string | undefined = undefined;
  if (product && !regionInfo.isLoading) {
    if (product.hasVariant && variantPath !== '') {
      const combination = product.variantPaths[variantPath]

      shownPrice = formatMoney(product.priceCurrency, combination.price[regionInfo.countryCode])
      hasStock = combination.stock > 0
    } else {
      shownPrice = formatMoney(product.priceCurrency, product.price[regionInfo.countryCode])
      hasStock = product.stock > 0
    }
  }

  let productRating = product.rating
  if (product.sourceMarketPlace === 'local') {
    productRating = 100
  }

  const content = <React.Fragment>
    <style jsx>{`
      .desc {
        font-size: 0.9rem;
      }
    `}</style>
    <div className="row">
      <div className="col-lg-6">
        <div className={styles.imageContainer}>
          <img
            src={imageUrl(product.images[mainImage])}
            alt="product image"
            onError={imageErrorHandler}
            fetchPriority="high"
          />
        </div>
        <div className={styles.imagePreviews}>
          {product.images.map((url, i) => {
            url = thumbUrl(url)
            return <img
              key={i}
              src={url}
              className="img-fluid"
              style={{ display: 'inline-block', width: '100px' }}
              onMouseEnter={() => setMainImage(i)}
              alt={`preview image ${i + 1}`}
              onError={imageErrorHandler}
            />
          })}
        </div>
      </div>
      <div className="col-lg-6">
        <div className="row">
          <div className="col text-uppercase text-muted align-self-center">
            <SourceLabel
              product={product}
            />
          </div>
          <div className="col-auto text-primary">
            {tfaPassed && (
              <button className="btn btn-link p-0" onClick={handleWishlist}>
                {wishlisted ? (
                  <AiFillHeart />
                )
                  : (
                    <AiOutlineHeart />
                  )}
              </button>
            )}
          </div>
        </div>
        <h2>{product.name}</h2>
        <Ratings
          ratings={productRating}
          className={styles.ratings}
        />
        {' | '}
        <span className="font-weight-bold">{product.soldCount || '0'}</span>
        {' '}
        <span className="font-weight-semibold text-muted">Sold</span>
        <div>
          Availability:
          {hasStock ? (
            <span className="badge text-bg-success ml-2">In Stock</span>
          ) : (
            <span className="badge text-bg-danger ml-2">Out of Stock</span>
          )}
        </div>

        <h3>
          {shownPrice || <Skeleton />}
        </h3>

        {isFoodCategory && (
          <OrderFoodMessage />
        )}

        {product.hasVariant && (
          <React.Fragment>
            {product.variants.map((variant, variantIndex) => (
              <div className="form-group" key={variantIndex}>
                <label className="form-label">{variant.name}</label>
                <br />
                <div className={`btn-group ${styles.productVariant}`}>
                  {selectedOptions ? (
                    variant.options.map((option, optionIndex) => {
                      const uniqueKey = `${variantIndex}-${optionIndex}`;
                      const id = `radio-variant-${uniqueKey}`;
                      const checked = selectedOptions[variantIndex][optionIndex];
                      const itemClass = classNames('btn', 'btn-outline-secondary', { active: checked });
                      return <React.Fragment key={uniqueKey}>
                        <input
                          type="radio"
                          name={id}
                          id={id}
                          value={optionIndex}
                          checked={checked}
                          onChange={handleOptionCheck(variantIndex, optionIndex)}
                          className="btn-check"
                        />
                        <label className={itemClass} htmlFor={id}>
                          {option.value}
                        </label>
                      </React.Fragment>
                    })
                  ) : (
                    <Skeleton />
                  )}
                </div>
              </div>
            ))}

          </React.Fragment>
        )}

        <div className="form-group">
          <label className="form-label">Quantity</label>
          <CartQuantitySelect
            value={quantity}
            onChange={setQuantity}
            maxQuantity={maxQuantity}
            minQuantity={minQuantity}
            disabled={!hasStock}
          />
        </div>

        {!flags?.isBlocked && (
            <button
              className="btn btn-link"
              onClick={() => setShowAddMessage(!showAddMessage)}
            >
              Add Order Instructions
            </button>
          )}
        <br />
        {showAddMessage && (
          <div className="form-group">
            <label className="form-label">Message</label>
            <CartMessageInput
              value={addMessage}
              onChange={genericTextChange(setAddMessage)}
            />
          </div>
        )}
        <ProductButtons
          product={product}
          quantity={quantity}
          variantPath={variantPath}
          message={showAddMessage ? addMessage : ''}
          disabled={!hasStock || product.disabled}
          flags={flags}
        />
        {product.disabled && (
          <div className="text-muted">
            The product is currently unavailable, please try again later.
          </div>
        )}
      </div>
    </div>

    <UnderlineNav
      navItems={NAV_ITEMS}
      activeIndex={activeProductNav}
      onChange={setActiveProductNav}
    />

    {activeProductNav === 0 && (
      <div>
        <p className={`${styles.description} desc`}>
          {product.description}
        </p>
        <label className="d-inline-block form-label">Product Specifications & Disclaimers</label>
        <dl className={`row desc ${styles.specifications}`}>
          <dt className="col-md-3">Condition</dt>
          <dd className="col-md-9">{product.conditionNew ? 'New' : 'Used'}</dd>
          <div className={`col-12 ${styles.specifications_divider}`} />

          <dt className="col-md-3">Weight</dt>
          <dd className="col-md-9">{`${product.weight}${product.weightUnit}`}</dd>
          <div className={`col-12 ${styles.specifications_divider}`} />

          <dt className="col-md-3">Disclaimer</dt>
          {/* <dd className="col-md-9">{product.disclaimer}</dd> */}
          <dd className="col-md-9 justify-content-start">Please note that the actual product you receive may vary slightly in appearance from the images shown. Differences in color, size, or other minor details may occur due to factors like the manufacturing process or photography lighting.<br />
            While we strive for accurate representation, the item delivered may not be an exact visual match to the provided imagery. This disclaimer applies across all products offered.<br />
            <br />
            For Used Items: Kindly be aware that pre-owned products in USED condition may differ in both appearance and functionality compared to the product images. <br />
            Returns and refunds are not available for these pre-owned items. By proceeding with the order, you acknowledge and agree to this disclaimer.</dd>
          <div className={`col-12 ${styles.specifications_divider}`} />
        </dl>
      </div>
    )}

    {activeProductNav === 1 && (
      <dl className={`row desc ${styles.specifications}`}>
        <dt className="col-md-3">Condition</dt>
        <dd className="col-md-9">{product.conditionNew ? 'New' : 'Used'}</dd>
        <div className={`col-12 ${styles.specifications_divider}`} />

        <dt className="col-md-3">Weight</dt>
        <dd className="col-md-9">{`${product.weight}${product.weightUnit}`}</dd>
        <div className={`col-12 ${styles.specifications_divider}`} />

        <dt className="col-md-3">Disclaimer</dt>
        {/* <dd className="col-md-9">{product.disclaimer}</dd> */}
        <dd className="col-md-9">Please note that the actual product you receive may vary slightly in appearance from the images shown. Differences in color, size, or other minor details may occur due to factors like the manufacturing process or photography lighting.<br />
          While we strive for accurate representation, the item delivered may not be an exact visual match to the provided imagery. This disclaimer applies across all products offered.<br />
          <br />
          For Used Items: Kindly be aware that pre-owned products in USED condition may differ in both appearance and functionality compared to the product images. <br />
          Returns and refunds are not available for these pre-owned items. By proceeding with the order, you acknowledge and agree to this disclaimer.</dd>
        <div className={`col-12 ${styles.specifications_divider}`} />
      </dl>
    )}

    {productRecommendations.length > 0 && (
      <>
        <UnderlineNav
          navItems={['Recommendations']}
          activeIndex={0}
          onChange={() => {}}
        />

        <div className="d-none d-md-block">
          <HorizontalScrollingProducts
            products={productRecommendations}
            columns={6}
          />
        </div>
        <div className="row d-md-none">
          {chunk(productRecommendations, 2).map((products, index) => {
            const productsFilled = new Array<ProductListing | null>(2).fill(null);
            for (let i = 0; i < products.length; i++) {
              productsFilled[i] = products[i];
            }

            return (
              <React.Fragment key={index}>
                {productsFilled.map((product, index) => {
                  if (!product) {
                    return <div className="col" key={index}></div>;
                  }

                  return (
                    <div className="col" key={product.name}>
                      <ProductListingCard product={product}/>
                    </div>
                  );
                })}
                <div className="w-100"></div>
              </React.Fragment>
            );
          })}
        </div>
      </>

    )}
  </React.Fragment>

  return <Layout>
    <HeadTitle title={product.name}>
      <MetaTags
        title={product.name}
        url={props.pageUrl}
        description={product.description}
        image={metaImageUrl}
      />
    </HeadTitle>

    {previousPages.length === 0 ? (
      <Skeleton className="mb-3" />
    ) : (
      <Breadcrumbs
        currentPage={product.name}
        previousPages={previousPages}
      />
    )}

    <TimeoutAlert
      message={errorMessage}
      onHide={() => setErrorMessage(null)}
    />
    {content}
  </Layout>
}

interface SingleProductPageParams extends ParsedUrlQuery {
  paramName: string;
}

export const getStaticProps = wrapper.getStaticProps(store => handleCountryCodeCases(async (context) => {
  const params = context.params as SingleProductPageParams;
  await staticProps.populateServerSideStore(store.dispatch)
  const base = await getSingleProductStaticProps(context)
  if ('props' in base) {
    const regionProps = staticProps.countryCode(params)
    base.props.pageUrl = base.props.pageUrl.replace('/product', `/${regionProps.countryCode}/product`);
  }


  return base
}, (context) => {
  const params = context.params as SingleProductPageParams
  return urljoin('product', params.paramName)
}))

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: true
  }
}

export default SingleProductPage
